import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import axios from "../api/axiosInstance";
import { bootstrapUserSession, meApi } from "../api/auth";
import { clearContextStorage } from "../utils/contextStorage";
import { clearStoredUserSession } from "../utils/userSession";

const COLORS = ["#2563eb", "#0f766e", "#f59e0b", "#ef4444", "#8b5e34", "#7c3aed", "#14b8a6"];
const box = {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 22,
    padding: 20,
    boxShadow: "0 12px 32px rgba(15,23,42,.06)"
};

const readJson = (key, fallback = null) => {
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

const fmtNum = (value) => new Intl.NumberFormat("en-IN").format(Number(value || 0));

const parseDate = (value) => {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return new Date(text);
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
        const [d, m, y] = text.split("/");
        return new Date(`${y}-${m}-${d}`);
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const fmtDate = (value, options = { day: "2-digit", month: "short", year: "numeric" }) => {
    const date = value instanceof Date ? value : parseDate(value);
    if (!date || Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", options);
};

const getUserName = (user) =>
    user?.fullName || user?.name || user?.employeeName || user?.email || "Admin User";

const Dashboard = () => {
    const [clock, setClock] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);
    const [user, setUser] = useState(() => readJson("user", null));
    const [summary, setSummary] = useState({ counts: {}, charts: {}, recentProjects: [] });

    const handleLogout = () => {
        localStorage.removeItem("token");
        clearStoredUserSession();
        clearContextStorage();
        window.location.href = "/login";
    };

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError("");

        let nextUser = readJson("user", null);
        try {
            nextUser = await bootstrapUserSession().catch(async () => {
                const fallbackUser = await meApi();
                localStorage.setItem("user", JSON.stringify(fallbackUser));
                return fallbackUser;
            });
        } catch {
            // keep cached user
        }
        setUser(nextUser);

        const context = readJson("context", {});
        const centerId = String(context?.centerId || "").trim();
        const url = centerId
            ? `/api/dashboard/summary?centerId=${encodeURIComponent(centerId)}`
            : "/api/dashboard/summary";

        try {
            const res = await axios.get(url);
            setSummary({
                counts:         res.data?.counts         || {},
                charts:         res.data?.charts         || {},
                recentProjects: res.data?.recentProjects || []
            });
            setLastUpdated(new Date());
            setError("");
        } catch {
            setError("Dashboard data could not be loaded. Please try refreshing.");
        }
        setLoading(false);
    }, []);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);
    useEffect(() => {
        const timer = window.setInterval(() => setClock(new Date()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    const context  = readJson("context", {});
    const c        = summary.counts;
    const charts   = summary.charts;
    const recentProjects = summary.recentProjects || [];

    // Derived counts (computed in frontend from pre-aggregated numbers)
    const activeDepartments    = Number(c.activeDepartments    || 0);
    const activeFunders        = Number(c.activeFunders        || 0);
    const activeProjects       = Number(c.activeProjects       || 0);
    const inactiveDepartments  = Number(c.departments || 0) - activeDepartments;
    const inactiveFunders      = Number(c.funders     || 0) - activeFunders;
    const inactiveProjects     = Number(c.projects    || 0) - activeProjects;
    const centersWithoutMapping = Math.max(Number(c.centers || 0) - Number(c.mappedCenters || 0), 0);

    const total = Number(c.candidates || 1);
    const pct = (val) => `${Math.round((Number(val || 0) / total) * 100)}%`;

    const masterCards = [
        ["Departments", c.departments || 0, "ti ti-building-community", "#2563eb", `${activeDepartments} active`],
        ["Funders",     c.funders     || 0, "ti ti-wallet",              "#0f766e", `${activeFunders} active`],
        ["Projects",    c.projects    || 0, "ti ti-briefcase-2",         "#f59e0b", `${activeProjects} active`],
        ["Centers",     c.centers     || 0, "ti ti-school",              "#ef4444", `${Number(c.centers || 0)} live`],
        ["Roles",       c.roles       || 0, "ti ti-shield-half",         "#8b5e34", "Access design"]
    ];

    const opsCards = [
        ["User Mappings", c.userMappings || 0, "ti ti-user-cog",    "#2563eb", `${Number(c.mappedCenters || 0)} centers mapped`],
        ["Trainers",      c.trainers    || 0, "ti ti-user-star",    "#1d4ed8", "Training workforce"],
        ["Assessors",     c.assessors   || 0, "ti ti-user-check",   "#0f766e", "Assessment workforce"],
        ["Courses",       c.courses     || 0, "ti ti-book",         "#f59e0b", "Available catalog"],
        ["Batches",       c.batches     || 0, "ti ti-layout-grid",  "#ef4444", "Execution units"],
        ["Candidates",    c.candidates  || 0, "ti ti-users",        "#7c3aed", "Beneficiary base"]
    ];

    const healthCards = [
        ["Training Completed", c.completedTraining || 0, pct(c.completedTraining), "#2563eb"],
        ["Certified",          c.certified         || 0, pct(c.certified),          "#0f766e"],
        ["Placed / Joined",    `${Number(c.placed || 0)} / ${Number(c.joined || 0)}`, "Employment outcomes", "#f59e0b"],
        ["Retained",           c.retained          || 0, "Month 1 working",         "#ef4444"]
    ];

    const watchlist = [
        ["Pending Enrollments",       c.pendingEnrollments  || 0, "Verification queue needs review"],
        ["Placed not joined",         c.notJoined           || 0, "Resolve joining blockers quickly"],
        ["Training dropouts",         c.dropouts            || 0, "Follow center-level recovery actions"],
        ["Centers without user map",  centersWithoutMapping,       "Assign accountable staff"],
        ["Projects ending in 60 days", c.endingSoonProjects || 0, "Track closure and extension readiness"],
        ["Inactive masters",          inactiveDepartments + inactiveFunders + inactiveProjects, "Check stale master records"]
    ];

    const projectsByDepartment = charts.projectsByDepartment || [];
    const centersByState       = charts.centersByState       || [];
    const usersByRole          = charts.usersByRole          || [];
    const candidatesByProject  = charts.candidatesByProject  || [];
    const topCenters           = charts.topCenters           || [];

    const quickLinks = [
        ["/departments",    "Departments", "Manage department master",    "ti ti-building-community"],
        ["/funders",        "Funders",     "Maintain funder master",      "ti ti-wallet"],
        ["/projects_manage","Projects",    "Track delivery portfolio",    "ti ti-briefcase-2"],
        ["/centers_manage", "Centers",     "Manage center setup",         "ti ti-school"],
        ["/candidates",     "Candidates",  "Review beneficiary records",  "ti ti-users"]
    ];

    return (
        <div style={{ minHeight: "100vh", padding: 28, background: "radial-gradient(circle at top left,#eff6ff 0%,#f8fafc 40%,#fff7ed 100%)", fontFamily: "Segoe UI, system-ui, sans-serif" }}>
            <style>{`
                .admin-action:hover{transform:translateY(-2px);box-shadow:0 16px 30px rgba(15,23,42,.10)}
                .admin-icon{display:inline-flex;align-items:center;justify-content:center;line-height:1;}
                .admin-icon i{font-family:tabler-icons !important;font-style:normal;font-variant:normal;font-weight:400;line-height:1;text-transform:none;display:inline-flex;align-items:center;justify-content:center;}
            `}</style>

            {/* Header */}
            <div style={{ ...box, marginBottom: 20, padding: 28, background: "linear-gradient(135deg,#0f172a 0%,#1d4ed8 50%,#f97316 100%)", color: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
                    <div>
                        <div style={{ display: "inline-block", padding: "6px 12px", borderRadius: 999, background: "rgba(255,255,255,.14)", fontSize: 12, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 12 }}>Admin Control Page</div>
                        <h1 style={{ margin: 0, fontSize: 34, fontWeight: 900 }}>Portfolio Dashboard</h1>
                        <div style={{ marginTop: 10, fontSize: 15, color: "rgba(255,255,255,.84)" }}>{`${getUserName(user)} | ${user?.email || "No email available"}`}</div>
                        <div style={{ marginTop: 8, fontSize: 14, color: "rgba(255,255,255,.78)" }}>{`Role: ${user?.role || "ADMIN"} | Context: ${context?.projectName || context?.project || "Global admin view"}`}</div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 18 }}>
                            {[
                                `FY: ${context?.fy || "All"}`,
                                `Department: ${context?.department || "All"}`,
                                `Project: ${context?.projectName || context?.project || "All"}`,
                                `Center: ${context?.centerId || "All"}`
                            ].map((item) => (
                                <span key={item} style={{ padding: "8px 12px", borderRadius: 999, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.18)", fontSize: 12, fontWeight: 700 }}>{item}</span>
                            ))}
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 28, fontWeight: 900 }}>{clock.toLocaleTimeString("en-IN")}</div>
                        <div style={{ marginTop: 6, fontSize: 13, color: "rgba(255,255,255,.8)" }}>{clock.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                        <div style={{ marginTop: 8, fontSize: 13, color: "rgba(255,255,255,.8)" }}>{`Last sync: ${lastUpdated ? fmtDate(lastUpdated) : "Waiting..."}`}</div>
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 16 }}>
                            <button style={{ padding: "10px 14px", borderRadius: 12, border: "none", background: "#fff", color: "#0f172a", fontWeight: 800, cursor: "pointer" }} onClick={loadDashboard} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
                            <Link to="/admin/choosecontext" style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,.28)", background: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 800, cursor: "pointer", textDecoration: "none" }}>Choose Context</Link>
                            <button style={{ padding: "10px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,.28)", background: "rgba(15,23,42,.22)", color: "#fff", fontWeight: 800, cursor: "pointer" }} onClick={handleLogout}>Logout</button>
                        </div>
                    </div>
                </div>
            </div>

            {error && <div style={{ ...box, marginBottom: 16, borderColor: "#fdba74", background: "#fff7ed", color: "#9a3412", fontWeight: 700 }}>{error}</div>}

            {/* Master cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 16, marginBottom: 16 }}>
                {masterCards.map(([label, value, icon, color, note]) => (
                    <div key={label} style={{ ...box, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
                            <div style={{ marginTop: 10, fontSize: 32, fontWeight: 900, color: "#111827" }}>{fmtNum(value)}</div>
                            <div style={{ fontSize: 13, color: "#6b7280" }}>{note}</div>
                        </div>
                        <div style={{ width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, background: `${color}18`, color }}>
                            <span className="admin-icon" aria-hidden="true"><i className={icon}></i></span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Operations cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 16, marginBottom: 16 }}>
                {opsCards.map(([label, value, icon, color, note]) => (
                    <div key={label} style={{ ...box, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
                            <div style={{ marginTop: 10, fontSize: 30, fontWeight: 900, color: "#111827" }}>{fmtNum(value)}</div>
                            <div style={{ fontSize: 13, color: "#6b7280" }}>{note}</div>
                        </div>
                        <div style={{ width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, background: `${color}18`, color }}>
                            <span className="admin-icon" aria-hidden="true"><i className={icon}></i></span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Health cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16, marginBottom: 18 }}>
                {healthCards.map(([label, value, note, color]) => (
                    <div key={label} style={box}>
                        <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
                        <div style={{ marginTop: 10, fontSize: 26, fontWeight: 900, color }}>{typeof value === "number" ? fmtNum(value) : value}</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>{note}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>

                {/* Projects by Department */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Project Portfolio by Department</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Where the current project load is concentrated</div>
                    </div>
                    {projectsByDepartment.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={projectsByDepartment}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#2563eb" radius={[10, 10, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No project data available.</div>
                    )}
                </div>

                {/* Users by Role */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>User Mapping by Role</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Visibility of center-level accountability</div>
                    </div>
                    {usersByRole.length ? (
                        <>
                            <ResponsiveContainer width="100%" height={230}>
                                <PieChart>
                                    <Pie data={usersByRole} dataKey="value" nameKey="name" outerRadius={85}>
                                        {usersByRole.map((item, index) => <Cell key={item.name} fill={COLORS[index % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            {usersByRole.map((item, index) => (
                                <div key={item.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: "50%", display: "inline-block", background: COLORS[index % COLORS.length] }} />
                                    <span>{item.name}</span>
                                    <strong style={{ marginLeft: "auto" }}>{item.value}</strong>
                                </div>
                            ))}
                        </>
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No user mapping data available.</div>
                    )}
                </div>

                {/* Candidates by Project */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Candidates by Project</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Beneficiary spread across active delivery streams</div>
                    </div>
                    {candidatesByProject.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={candidatesByProject}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#0f766e" radius={[10, 10, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No candidate distribution available.</div>
                    )}
                </div>

                {/* Centers by State */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Center Footprint</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Top states by center presence</div>
                    </div>
                    {centersByState.length ? (
                        centersByState.map((item, index) => (
                            <div key={item.name} style={{ marginBottom: 14 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                                    <span style={{ fontWeight: 700, color: "#111827" }}>{item.name}</span>
                                    <span style={{ fontSize: 13, color: "#6b7280" }}>{item.value}</span>
                                </div>
                                <div style={{ height: 10, borderRadius: 999, background: "#edf2f7", overflow: "hidden" }}>
                                    <div style={{ width: Number(c.centers || 1) ? `${Math.min((Number(item.value) / Number(c.centers)) * 100, 100)}%` : "0%", height: "100%", borderRadius: 999, background: COLORS[index % COLORS.length] }} />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No center data available.</div>
                    )}
                </div>

                {/* Admin Snapshot */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Admin Snapshot</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Quick operating picture across masters and execution</div>
                    </div>
                    {[
                        ["Mobilizations",       fmtNum(c.mobilizations   || 0)],
                        ["Enrollment Records",  fmtNum(c.enrollments     || 0)],
                        ["Attendance Records",  fmtNum(c.attendance      || 0)],
                        ["Other Activities",    fmtNum(c.otherActivities || 0)],
                        ["Internal Pass",       `${fmtNum(c.internalPass || 0)} (${pct(c.internalPass)})`],
                        ["External Pass",       `${fmtNum(c.externalPass || 0)} (${pct(c.externalPass)})`],
                        ["Certificates",        fmtNum(c.certified       || 0)],
                        ["Placements",          fmtNum(c.placed          || 0)]
                    ].map(([label, value]) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                            <span style={{ fontSize: 13, color: "#6b7280" }}>{label}</span>
                            <strong>{value}</strong>
                        </div>
                    ))}
                </div>

                {/* Top Centers */}
                <div style={box}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Top Centers by Candidate Volume</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Centers currently carrying the highest beneficiary load</div>
                    </div>
                    {topCenters.length ? (
                        topCenters.map((item) => (
                            <div key={item.centerId} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "12px 14px", border: "1px solid #eef2f7", borderRadius: 14, background: "#fbfcfe", marginBottom: 10 }}>
                                <div>
                                    <div style={{ fontWeight: 800 }}>{item.name}</div>
                                    <div style={{ fontSize: 13, color: "#6b7280" }}>{item.centerId}</div>
                                </div>
                                <div style={{ fontWeight: 900 }}>{fmtNum(item.value)}</div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No center volume data available.</div>
                    )}
                </div>

                {/* Attention Needed */}
                <div style={{ ...box, gridColumn: "1 / -1" }}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Attention Needed</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Priority admin follow-ups across master data and delivery</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
                        {watchlist.map(([label, value, note]) => (
                            <div key={label} style={{ borderRadius: 18, border: "1px solid #eef2f7", background: "#fbfcfe", padding: 16 }}>
                                <div style={{ fontSize: 30, fontWeight: 900, color: "#1d4ed8", marginBottom: 8 }}>{fmtNum(value)}</div>
                                <div style={{ fontWeight: 800, color: "#111827" }}>{label}</div>
                                <div style={{ fontSize: 13, color: "#6b7280" }}>{note}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Projects */}
                <div style={{ ...box, gridColumn: "1 / -1" }}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>Recent Projects</div>
                        <div style={{ fontSize: 13, color: "#6b7280" }}>Latest project records and movement across the portfolio</div>
                    </div>
                    {recentProjects.length ? (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        {["Project", "Department", "Status", "Start", "End", "Target"].map((heading) => (
                                            <th key={heading} style={{ textAlign: "left", padding: 12, background: "#f8fafc", color: "#6b7280", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>{heading}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentProjects.map((project) => (
                                        <tr key={project.projectId}>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>
                                                <div style={{ fontWeight: 800 }}>{project.projectName || "-"}</div>
                                                <div style={{ fontSize: 13, color: "#6b7280" }}>{project.projectId || "-"}</div>
                                            </td>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>{project.department || "-"}</td>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>{project.status || "-"}</td>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>{fmtDate(project.projectStartDate)}</td>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>{fmtDate(project.projectEndDate)}</td>
                                            <td style={{ padding: 14, borderBottom: "1px solid #f1f5f9", fontSize: 14 }}>{fmtNum(project.totalProjectTarget || project.trainingTargets)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: "2rem 0", textAlign: "center", color: "#6b7280", fontWeight: 700 }}>No project records found.</div>
                    )}
                </div>
            </div>

            {/* Quick Links */}
            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>
                {quickLinks.map(([to, title, subtitle, icon]) => (
                    <Link key={to} className="admin-action" to={to} style={{ textDecoration: "none", color: "inherit", ...box, padding: 18, transition: "transform .18s ease, box-shadow .18s ease" }}>
                        <div style={{ width: 42, height: 42, borderRadius: 14, background: "#eff6ff", color: "#1d4ed8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                            <span className="admin-icon" aria-hidden="true"><i className={icon}></i></span>
                        </div>
                        <div style={{ fontWeight: 900, fontSize: 16, color: "#111827", marginTop: 14 }}>{title}</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>{subtitle}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default Dashboard;
